import { useState, useEffect } from 'react';
import { format, addMonths, subMonths } from 'date-fns';
import type { CalendarEvent } from '../utils/calendar-helpers';
import type { GetCaregiverCalendarResponse } from '../types/api.types';
import { transformCalendarToEvents } from '../utils/api-transforms';

/**
 * Hook to fetch calendar data for search functionality
 * Fetches a wider date range (past 6 months + future 6 months) for comprehensive search
 */
export function useCalendarSearchData(caregiverIds: string[]) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchSearchCalendars = async () => {
      if (caregiverIds.length === 0) {
        setEvents([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { homeNursingService } = await import('../../api/homeNursingClient');

        const today = new Date();

        // Fetch 12 months: 6 months back + current month + 5 months forward
        const monthsToFetch = [
          subMonths(today, 6),
          subMonths(today, 5),
          subMonths(today, 4),
          subMonths(today, 3),
          subMonths(today, 2),
          subMonths(today, 1),
          today,
          addMonths(today, 1),
          addMonths(today, 2),
          addMonths(today, 3),
          addMonths(today, 4),
          addMonths(today, 5),
        ];

        console.log('🔍 SEARCH: Fetching calendar data for search');
        console.log('Date range:', format(monthsToFetch[0], 'MMM yyyy'), '-', format(monthsToFetch[monthsToFetch.length - 1], 'MMM yyyy'));
        console.log('Caregivers:', caregiverIds.length);

        // Fetch all months for all caregivers in parallel
        const calendarPromises: Promise<GetCaregiverCalendarResponse>[] = [];

        caregiverIds.forEach(caregiverId => {
          monthsToFetch.forEach(monthDate => {
            calendarPromises.push(
              homeNursingService.getCaregiverCalendar(caregiverId, {
                view: 'month',
                date: format(monthDate, 'yyyy-MM-dd'),
              })
            );
          });
        });

        const calendars = await Promise.all(calendarPromises);

        console.log('🔍 SEARCH: API returned', calendars.length, 'calendar responses');

        // Transform all calendars to events
        const allEvents = calendars.flatMap((calendar: GetCaregiverCalendarResponse, index) => {
          const events = transformCalendarToEvents(calendar.data, calendar.data.caregiverId);
          if (index === 0) {
            console.log('🔍 SEARCH: First calendar sample:', {
              caregiverId: calendar.data.caregiverId,
              view: calendar.data.view,
              startDate: calendar.data.startDate,
              endDate: calendar.data.endDate,
              eventsCount: events.length,
              firstEvent: events[0],
              lastEvent: events[events.length - 1]
            });
          }
          return events;
        });

        console.log('🔍 SEARCH: Fetched', allEvents.length, 'total events for search');

        // Log date range of events
        if (allEvents.length > 0) {
          const dates = allEvents.map(e => e.startDate).sort((a, b) => a.getTime() - b.getTime());
          console.log('🔍 SEARCH: Event date range:', {
            earliest: format(dates[0], 'MMM d, yyyy'),
            latest: format(dates[dates.length - 1], 'MMM d, yyyy'),
            totalEvents: allEvents.length
          });
        }

        setEvents(allEvents);
        setError(null);
      } catch (err) {
        setError(err as Error);
        console.error('Failed to fetch search calendar data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchCalendars();
  }, [caregiverIds]);

  return { events, loading, error };
}
